import { Autocomplete, Checkbox, debounce } from '@mui/material'
import { productApis } from 'api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { TextField } from 'shared/components'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { ProductModel } from 'types/products'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'

interface ProductSelectionProps {
  multiple?: boolean
  onChange: (arr?: any) => void
}

const ProductSelection = ({
  onChange,
  multiple = false,
}: ProductSelectionProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const defaultValue = useMemo(() => (multiple ? [] : null), [multiple])
  const [value, setValue] = useState<any>(defaultValue)
  const [options, setOptions] = useState<Array<ProductModel>>([])

  const handleAsyncData = useCallback(async (searchText?: string) => {
    setIsLoading(true)
    const res = await productApis.getList({
      pageIndex: 0,
      pageSize: 20,
      allowChangeTem: 1,
      searchValue: searchText,
    })

    if (res.Result && res.Result.length) {
      setOptions(res.Result)
    }
    setIsLoading(false)
    return []
  }, [])

  const renderOptions = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: ProductModel,
      { selected }: { selected: boolean }
    ) => {
      const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
      const checkedIcon = <CheckBoxIcon fontSize="small" />
      return (
        <li {...props}>
          {multiple && (
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
          )}
          {`${option.ProductCode}-${option.ProductName}`}
        </li>
      )
    },
    [multiple]
  )

  const handleInputChange = useCallback((_e: any, val?: string) => {
    setSearchValue(val || '')
  }, [])

  const debounceInput = debounce(handleInputChange, 500)

  const handleChange = useCallback(
    (_e: any, val: any) => {
      onChange(val)
      setValue(val)
    },
    [onChange]
  )

  useEffect(() => {
    if (isLoading || (options && options.length)) {
      return
    }
    handleAsyncData()
  }, [isLoading, options, handleAsyncData])

  useEffect(() => {
    if (searchValue) {
      handleAsyncData(searchValue)
    }
  }, [searchValue, handleAsyncData])

  return (
    <Autocomplete
      sx={{
        width: 500,
      }}
      multiple={multiple}
      renderInput={params => (
        <TextField
          {...params}
          sx={{
            '& input': {
              padding: '0px !important',
            },
          }}
        />
      )}
      value={value}
      loading={isLoading}
      loadingText={'Loading...'}
      options={options}
      disableCloseOnSelect
      onInputChange={debounceInput}
      renderOption={renderOptions}
      filterOptions={x => x}
      onChange={handleChange}
      getOptionLabel={o => `${o.ProductName}`}
      isOptionEqualToValue={(o, v) => o.ProductCode === v.ProductCode}
    />
  )
}

export default ProductSelection
